import { AlertCircle, ArrowRight, KeyRound, Loader2, ShieldCheck, Siren, UserRoundPlus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getMe } from '../../api/admin'
import { login, register, AuthError } from '../../api/auth'
import { probeHealth } from '../../api/client'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'
import { useAuthStore } from '../../stores/auth'

type Mode = 'login' | 'register'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function validate(mode: Mode, email: string, password: string, displayName: string): string | null {
  if (!email.trim()) return '请输入邮箱'
  if (!EMAIL_PATTERN.test(email.trim())) return '邮箱格式不正确'
  if (!password) return '请输入密码'
  if (mode === 'register' && password.length < 8) return '注册密码至少 8 位'
  if (mode === 'register' && !displayName.trim()) return '请输入显示名称'
  return null
}

export function LoginPage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [backendUp, setBackendUp] = useState<boolean | null>(null)

  const [devToken, setDevToken] = useState('')
  const isDev = import.meta.env.DEV

  const checkBackend = () => {
    void probeHealth().then(setBackendUp)
  }

  const switchMode = (next: Mode) => {
    setMode(next)
    setError(null)
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const invalid = validate(mode, email, password, displayName)
    if (invalid) {
      setError(invalid)
      return
    }
    setLoading(true)
    setError(null)
    const action =
      mode === 'login'
        ? login(email.trim(), password)
        : register(email.trim(), password, displayName.trim())
    void action
      .then(() => {
        void navigate('/command', { replace: true })
      })
      .catch((err: unknown) => {
        if (err instanceof AuthError) {
          setError(
            err.status === 401
              ? '邮箱或密码错误'
              : err.status === 423
                ? '账户已锁定，请稍后再试'
                : err.status === 403
                  ? '账户已被禁用'
                  : err.status === 429
                    ? '尝试过于频繁，请稍后再试'
                    : err.message,
          )
        } else {
          setError(err instanceof Error ? err.message : '登录失败')
        }
      })
      .finally(() => setLoading(false))
  }

  // debug-only 折叠入口：粘贴开发签发的 HS256 令牌
  const submitDevToken = () => {
    const trimmed = devToken.trim()
    if (!trimmed) {
      setError('请粘贴开发令牌')
      return
    }
    if (trimmed.split('.').length !== 3) {
      setError('令牌格式无效：应为 xxx.yyy.zzz 三段 JWT')
      return
    }
    setLoading(true)
    setError(null)
    const prev = useAuthStore.getState().token
    useAuthStore.setState({ token: trimmed })
    getMe()
      .then((me) => {
        useAuthStore.setState({ token: trimmed, me })
        void navigate('/command', { replace: true })
      })
      .catch((err: unknown) => {
        useAuthStore.setState({ token: prev })
        setError(err instanceof Error ? err.message : '令牌校验失败')
      })
      .finally(() => setLoading(false))
  }

  return (
    <main className="relative grid min-h-screen place-items-center overflow-hidden bg-background p-6">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'linear-gradient(rgba(86,215,255,.06) 1px, transparent 1px), linear-gradient(90deg, rgba(86,215,255,.06) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
      <div className="absolute -left-40 top-[-20%] size-[560px] rounded-full bg-cyan-500/[.07] blur-[140px]" />
      <div className="absolute -right-40 bottom-[-20%] size-[560px] rounded-full bg-rose-500/[.06] blur-[140px]" />

      <section className="panel-glass relative w-full max-w-lg rounded-2xl p-7 shadow-2xl">
        <div className="mb-6 flex items-center gap-3">
          <span className="grid size-11 place-items-center rounded-xl bg-primary/15 text-primary">
            <Siren size={22} />
          </span>
          <div>
            <h1 className="text-lg font-semibold tracking-[.2em] text-foreground">SEVEN MIRROR</h1>
            <p className="text-xs text-muted-foreground">天穹统一指挥与管理平台</p>
          </div>
        </div>

        <div className="flex gap-1 rounded-lg border border-border bg-black/20 p-1 text-xs" role="tablist" aria-label="登录或注册">
          {(
            [
              ['login', '账号登录'],
              ['register', '注册账号'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={mode === key}
              onClick={() => switchMode(key)}
              className={`flex-1 rounded-md px-3 py-1.5 font-medium transition ${
                mode === key ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <h2 className="mt-5 text-xl font-semibold text-foreground">
          {mode === 'login' ? '欢迎回来' : '创建指挥中心账号'}
        </h2>
        <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
          {mode === 'login'
            ? '使用平台账号登录，会话通过 accessToken / refreshToken 管理，过期自动静默续期。'
            : '注册后即可登录指挥中心；权限与区域范围由管理员分配。'}
        </p>

        <form className="mt-5 space-y-3" onSubmit={submit} noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="email">邮箱</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operator@seven.example"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">密码</Label>
            <Input
              id="password"
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === 'register' ? '至少 8 位' : '输入密码'}
            />
          </div>
          {mode === 'register' && (
            <div className="space-y-1.5">
              <Label htmlFor="displayName">显示名称</Label>
              <Input
                id="displayName"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="指挥员"
              />
            </div>
          )}

          {error && (
            <div role="alert" className="flex items-start gap-2 rounded-lg bg-[var(--critical-soft)] px-3 py-2 text-xs text-critical">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <Button className="w-full" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> {mode === 'login' ? '正在登录…' : '正在注册…'}
              </>
            ) : mode === 'login' ? (
              <>
                <KeyRound size={16} /> 登录并进入指挥中心 <ArrowRight size={16} />
              </>
            ) : (
              <>
                <UserRoundPlus size={16} /> 注册并进入指挥中心 <ArrowRight size={16} />
              </>
            )}
          </Button>
        </form>

        {isDev && (
          <details className="mt-5 rounded-lg border border-border bg-black/20 px-3.5 py-3 text-[11px] leading-5 text-muted-foreground">
            <summary className="cursor-pointer select-none text-xs font-medium text-foreground/80">
              开发调试：粘贴本地签发令牌
            </summary>
            <p className="mt-2">
              本地未起账号服务时，可用签发脚本生成 HS256 令牌直接进入（仅 dev 环境显示）。权限与区域范围完全由服务端校验。
            </p>
            <Textarea
              aria-label="开发令牌"
              value={devToken}
              onChange={(e) => setDevToken(e.target.value)}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
              className="mt-2 min-h-[72px] font-mono text-[11px] leading-4"
              spellCheck={false}
            />
            <Button variant="outline" size="sm" className="mt-2 w-full" type="button" onClick={submitDevToken} disabled={loading}>
              <KeyRound size={14} /> 校验开发令牌并进入
            </Button>
            <pre className="mt-2.5 overflow-x-auto rounded-md bg-black/40 p-2.5 font-mono text-[10px] leading-4 text-watch">
{`export DEV_JWT_SECRET='seven-dev-secret-please-change-0123456789'
bash backend/run.sh
node scripts/issue-dev-token.mjs --sub admin-1 --name 指挥员 --super`}
            </pre>
          </details>
        )}

        <div className="mt-5 flex items-center justify-between rounded-lg border border-border bg-black/20 px-3.5 py-2.5">
          <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <ShieldCheck size={14} className={backendUp ? 'text-success' : 'text-muted-foreground/50'} />
            后端服务（:8080）
          </span>
          {backendUp == null ? (
            <Button variant="ghost" size="sm" type="button" onClick={checkBackend}>
              探测
            </Button>
          ) : (
            <span className={`flex items-center gap-1.5 text-[11px] ${backendUp ? 'text-success' : 'text-critical'}`}>
              <span className={`size-1.5 rounded-full ${backendUp ? 'bg-success' : 'bg-critical'}`} />
              {backendUp ? '在线' : '未启动'}
            </span>
          )}
        </div>

        <p className="mt-4 text-center text-[10px] leading-4 text-muted-foreground/60">
          accessToken 仅保存在内存中，refreshToken 独立存储；退出登录将同时吊销服务端会话。
        </p>
      </section>
    </main>
  )
}
